app.controller('emailsReceivedController', function ($rootScope, $scope, $timeout, safeApply, emailRepliesService) {

  /*
    Emails Received
    Replies to emails we sent through SendGrid land in the API's Inbound Parse webhook and are stored in
    z_email_replies (with the outbound email they answer in z_email_threads). This panel lists them and opens
    one in a modal, rendering the HTML body inside a sandboxed iframe so email markup can't touch the CRM.

    Statuses: new -> read (set automatically when opened) -> processed | archived. 'unmatched' means the reply
    address could not be tied to an email we sent (the source is still known from the address prefix).
  */

  const MODAL_ID = 'email_reply_modal'
  const FRAME_ID = 'email_reply_frame'
  const LIST_LIMIT = 500

  const SOURCE_LABELS = { CRM: 'CRM', Dashboard: 'Dashboard', Evidence_Portal: 'Evidence Portal', CAD_SYNC: 'CAD Sync' }

  let modalRef = null

  // Every apply goes through here: loads and the modal's hidden event can complete after the user has
  // navigated away, and safeApply on a destroyed scope throws ($root is null).
  const apply = () => { if (!$scope.$$destroyed) safeApply($scope) }

  $scope.loading = false
  $scope.loadError = null
  $scope.replies = []

  $scope.statusOptions = [
    { key: 'open', label: 'Open' },            // everything that is not archived
    { key: 'new', label: 'New' },
    { key: 'read', label: 'Read' },
    { key: 'processed', label: 'Processed' },
    { key: 'unmatched', label: 'Unmatched' },
    { key: 'archived', label: 'Archived' },
    { key: 'all', label: 'All' },
  ]
  $scope.sourceOptions = [
    { key: 'all', label: 'All sources' },
    { key: 'CRM', label: 'CRM' },
    { key: 'Dashboard', label: 'Dashboard' },
    { key: 'Evidence_Portal', label: 'Evidence Portal' },
    { key: 'CAD_SYNC', label: 'CAD Sync' },
  ]
  $scope.rangeOptions = [
    { days: 7, label: 'Last 7 days' },
    { days: 30, label: 'Last 30 days' },
    { days: 90, label: 'Last 90 days' },
    { days: 0, label: 'All time' },
  ]

  $scope.filters = { status: 'open', source: 'all', days: 30, search: '' }

  $scope.modal = { open: false, loading: false, error: null, reply: null, view: 'html' }

  // ---------------------------------------------------------------------------
  // Display helpers
  // ---------------------------------------------------------------------------

  $scope.formatDate = (val) => {
    if (!val) return ''
    return moment(val).format('MM/DD/YYYY h:mm A')
  }

  $scope.fromNow = (val) => {
    if (!val) return ''
    return moment(val).fromNow()
  }

  $scope.sourceLabel = (source) => SOURCE_LABELS[source] || source || 'Unknown'

  $scope.fromDisplay = (row) => {
    if (!row) return ''
    if (row.from_name && row.from_email) return `${row.from_name} <${row.from_email}>`
    return row.from_email || row.from_name || ''
  }

  // Badge colour per status - classes live in _emails_received.scss
  $scope.statusClass = (status) => `er-badge er-badge-${status || 'new'}`

  $scope.isUnmatched = (row) => !!row && !row.thread_id

  $scope.copy = async (value) => {
    if (!value) return
    try {
      await navigator.clipboard.writeText(String(value))
      toastr.success('Copied to clipboard')
    } catch (err) {
      console.log('copy failed :>> ', err)
      toastr.error('Could not copy')
    }
  }

  // Deep link into the client record: /{user_id}/{url_password}/{target_user_id} (see crmController)
  $scope.canOpenClient = (row) => !!(row && row.user_id && $rootScope.user_info)
  $scope.openClient = (row) => {
    if (!$scope.canOpenClient(row)) return
    const user = $rootScope.user_info
    window.open(`/${user.id}/${user.url_password}/${row.user_id}`, '_blank')
  }

  // ---------------------------------------------------------------------------
  // Filtering
  // ---------------------------------------------------------------------------

  const matchesSourceAndSearch = (row) => {
    const f = $scope.filters
    if (f.source !== 'all' && row.source !== f.source) return false
    if (f.search) {
      const term = f.search.trim().toLowerCase()
      if (term) {
        const haystack = [row.from_email, row.from_name, row.subject, row.sent_subject, row.sent_to, row.user_id, row.reference, row.token]
          .map((v) => (v === null || v === undefined) ? '' : String(v))
          .join(' ')
          .toLowerCase()
        if (!haystack.includes(term)) return false
      }
    }
    return true
  }

  const matchesStatus = (row, statusKey) => {
    if (statusKey === 'all') return true
    if (statusKey === 'open') return row.status !== 'archived'
    return row.status === statusKey
  }

  $scope.rowsFilter = (row) => matchesStatus(row, $scope.filters.status) && matchesSourceAndSearch(row)

  // Count shown on each status pill - respects the source / search filters so the numbers add up to what is listed
  $scope.statusCount = (statusKey) => {
    let count = 0
    for (const row of $scope.replies) {
      if (matchesStatus(row, statusKey) && matchesSourceAndSearch(row)) count++
    }
    return count
  }

  $scope.visibleCount = () => $scope.statusCount($scope.filters.status)

  $scope.setStatusFilter = (key) => { $scope.filters.status = key }

  $scope.setRange = (days) => {
    if ($scope.filters.days === days) return
    $scope.filters.days = days
    $scope.refresh()
  }

  $scope.clearSearch = () => { $scope.filters.search = '' }

  // ---------------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------------

  // attachments arrive as a JSON column - an array from the API, but a string if anything re-serialised it
  function normalizeRow(row) {
    if (!row) return row
    if (typeof row.attachments === 'string') {
      try { row.attachments = JSON.parse(row.attachments) } catch (error) { row.attachments = [] }
    }
    if (!Array.isArray(row.attachments)) row.attachments = []
    row.attachment_count = Number(row.attachment_count) || row.attachments.length
    if (typeof row.metadata === 'string') {
      try { row.metadata = JSON.parse(row.metadata) } catch (error) { /* leave as-is */ }
    }
    return row
  }

  const errorText = (result, fallback) => {
    const err = result && result.data ? result.data.error : null
    if (!err) return fallback
    return err.message || err.sqlMessage || (typeof err === 'string' ? err : fallback)
  }

  $scope.refresh = async () => {
    if ($scope.loading) return
    $scope.loading = true
    $scope.loadError = null
    apply()

    const params = { limit: LIST_LIMIT }
    if ($scope.filters.days > 0) params.since = moment().subtract($scope.filters.days, 'days').format('YYYY-MM-DD HH:mm:ss')

    try {
      const result = await emailRepliesService.list(params)
      if (result.data.success) {
        $scope.replies = (result.data.data || []).map(normalizeRow)
      } else {
        $scope.loadError = errorText(result, 'Unable to load emails')
        console.log('emailReplies.list failed :>> ', result.data.error)
      }
    } catch (error) {
      $scope.loadError = error.message
      console.log('emailReplies.list error :>> ', error)
    }

    $scope.loading = false
    apply()
  }

  // ---------------------------------------------------------------------------
  // Modal
  // ---------------------------------------------------------------------------

  function getModal() {
    if (!modalRef) {
      const el = document.getElementById(MODAL_ID)
      modalRef = new bootstrap.Modal(el)
      el.addEventListener('hidden.bs.modal', () => {
        $scope.modal.open = false
        apply()
      })
    }
    return modalRef
  }

  const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])

  // The iframe is sandboxed (no scripts, no same-origin), so this document is purely presentational.
  // <base target="_blank"> lets links in the email open a new tab instead of navigating the frame.
  function buildDocument(reply) {
    const body = reply.html_body
      ? reply.html_body
      : `<pre class="er-text">${escapeHtml(reply.text_body || '(this email has no content)')}</pre>`
    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <base target="_blank">
  <style>
    body { margin: 14px 18px; font-family: Poppins, Arial, Helvetica, sans-serif; font-size: 14px; line-height: 1.5; color: #1f2933; word-wrap: break-word; }
    img { max-width: 100%; height: auto; }
    blockquote { border-left: 3px solid #d4dae3; margin: 8px 0; padding: 2px 0 2px 12px; color: #6c757d; }
    pre.er-text { white-space: pre-wrap; font-family: inherit; margin: 0; }
    a { color: #2b9cd3; }
  </style>
</head>
<body>${body}</body>
</html>`
  }

  function renderFrame(reply) {
    // wait a digest so the modal DOM is in place
    $timeout(() => {
      const frame = document.getElementById(FRAME_ID)
      if (frame) frame.srcdoc = buildDocument(reply)
    }, 0)
  }

  $scope.openReply = async (row) => {
    $scope.modal = { open: true, loading: true, error: null, reply: row, view: 'html' }
    getModal().show()
    apply()

    try {
      const result = await emailRepliesService.get({ id: row.id })
      if (!result.data.success) throw new Error(errorText(result, 'Unable to load this email'))
      const full = normalizeRow(result.data.data)
      $scope.modal.reply = full
      $scope.modal.view = full.html_body ? 'html' : 'text'
      renderFrame(full)
      // Opening a new reply is what "reading" it means
      if (full.status === 'new') $scope.setStatus(full, 'read', { silent: true })
    } catch (error) {
      console.log('openReply error :>> ', error)
      $scope.modal.error = error.message
    }

    $scope.modal.loading = false
    apply()
  }

  $scope.setView = (view) => { $scope.modal.view = view }

  $scope.closeModal = () => { if (modalRef) modalRef.hide() }

  function applyStatus(id, status) {
    const row = $scope.replies.find((r) => r.id === id)
    if (row) row.status = status
    if ($scope.modal.reply && $scope.modal.reply.id === id) $scope.modal.reply.status = status
  }

  $scope.setStatus = async (reply, status, opts = {}) => {
    if (!reply || reply.status === status) return
    try {
      const result = await emailRepliesService.updateStatus({ id: reply.id, status: status })
      if (!result.data.success) throw new Error(errorText(result, 'Update failed'))
      applyStatus(reply.id, status)
      if (!opts.silent) toastr.success(`Marked as ${status}`)
    } catch (error) {
      console.log('setStatus failed :>> ', error)
      if (!opts.silent) toastr.error(`Could not update status: ${error.message}`)
    }
    apply()
  }

  $scope.$on('$destroy', () => {
    if (modalRef) {
      modalRef.hide()
      modalRef.dispose()
      modalRef = null
    }
  })

  $scope.refresh()

})
